/**
 * Pure helpers for building-label rendering. Kept independent of Pixi so they
 * can be unit-tested in a node environment — the truncate function takes a
 * width-measuring callback rather than calling Pixi.TextMetrics directly. The
 * caller (building-layer.ts) supplies the Pixi measurer at runtime.
 */

export const ELLIPSIS = '…'

/** Min characters of original text we'll keep alongside the ellipsis. Below
 *  this, we'd be rendering just `…` which is uninformative; better to hide. */
const MIN_KEPT_CHARS = 1

/**
 * Rotation rule. Strict height > width → vertical (rotated 90° CCW so it
 * reads bottom-to-top with the head tilted left). Squares and wider
 * footprints stay horizontal; we flip wide-but-short labels rather than
 * rotating them. Keeps text legible at all zoom levels — a 2×2 reads
 * horizontal, a 1×3 reads vertical.
 */
export function shouldRotate(width: number, height: number): boolean {
  return height > width
}

/**
 * Binary-search the longest prefix of `text` that, when concatenated with
 * the ellipsis, still fits within `maxWidth`. If even the smallest prefix
 * doesn't fit, returns the empty string — caller is expected to either hide
 * the label or fall back to a shorter form.
 *
 * `measureWidth` is supplied by the caller so this function stays
 * Pixi-agnostic (in tests we pass a mock; in production we pass a closure
 * over Pixi.TextMetrics.measureText).
 */
export function truncateToFit(
  text: string,
  measureWidth: (s: string) => number,
  maxWidth: number,
): string {
  if (!text) return ''
  if (maxWidth <= 0) return ''
  if (measureWidth(text) <= maxWidth) return text

  // Binary search: find the largest k such that text[0..k] + ellipsis fits.
  let lo = 0
  let hi = text.length - 1
  let bestK = -1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    const candidate = text.slice(0, mid + 1) + ELLIPSIS
    if (measureWidth(candidate) <= maxWidth) {
      bestK = mid
      lo = mid + 1
    } else {
      hi = mid - 1
    }
  }
  if (bestK < MIN_KEPT_CHARS - 1) return ''
  return text.slice(0, bestK + 1) + ELLIPSIS
}

/**
 * The "abbreviated" middle-tier label: first 2-3 characters + ellipsis,
 * regardless of whether the full label would have fit. Words are not
 * collapsed — for "Yukitomo Emporium" you get "Yu…", not "YE".
 */
export function abbreviateShort(text: string): string {
  if (!text) return ''
  const trimmed = text.trim()
  if (trimmed.length === 0) return ''
  if (trimmed.length <= 2) return trimmed
  return trimmed.slice(0, 2) + ELLIPSIS
}

/**
 * Standard sRGB relative luminance, per WCAG. Used to decide whether a
 * building's category fill is light enough to take dark text or dark
 * enough to take light text.
 */
export function relativeLuminance(rgb: { r: number; g: number; b: number }): number {
  const channel = (c: number): number => {
    const cs = c / 255
    return cs <= 0.03928 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b)
}

const TEXT_COLOUR_LIGHT = 0xf5f5f5  // near-white, slightly softened
const TEXT_COLOUR_DARK = 0x1a1a1a   // near-black, slightly softened
const LUMINANCE_THRESHOLD = 0.5

/**
 * Pick a contrast-aware text colour for a building given its category fill
 * (encoded as a 0xRRGGBB number — what Pixi consumes). Light fills get dark
 * text, dark fills get light text. The single threshold at 0.5 is a
 * luminance midpoint — fills above 0.5 get dark text, fills below get light
 * text. Simple, predictable, and avoids per-fill tuning. Per-building rather
 * than per-theme so we never end up with an unreadable label on a deep navy
 * or maroon building just because the theme defaults to dark text.
 */
export function pickTextColour(fillNumber: number): number {
  const r = (fillNumber >> 16) & 0xff
  const g = (fillNumber >> 8) & 0xff
  const b = fillNumber & 0xff
  return relativeLuminance({ r, g, b }) >= LUMINANCE_THRESHOLD
    ? TEXT_COLOUR_DARK
    : TEXT_COLOUR_LIGHT
}

/** Tier thresholds for the on-screen visibility rule. */
export const TIER_FULL_PX = 28
export const TIER_SHORT_PX = 14

export type LabelTier = 'full' | 'short' | 'hidden'

/**
 * Decide which tier a building should render in, given its smallest
 * dimension in screen pixels (cells × cellSize × worldScale).
 */
export function tierFor(smallestScreenPx: number): LabelTier {
  if (smallestScreenPx >= TIER_FULL_PX) return 'full'
  if (smallestScreenPx >= TIER_SHORT_PX) return 'short'
  return 'hidden'
}
